<template>
  <div class="md:ml-12 max-w-600 my-8 mb-12">
    <div class="label">
      <img src="./assets/choice.svg" alt="选择题" />
      <span class="px-2">{{ inputType === 'radio' ? '单选题' : '多选题' }}</span>
    </div>
    <div class="mb-4">
      <h1 class="font-normal title-text text-lg whitespace-pre-wrap">
        <span>{{ content.title.trim() }}</span>
        <span v-if="totalScore">({{ totalScore }}分)</span>
      </h1>
    </div>

    <form :class="['content', warningClass]" @submit.prevent="handleSubmit">
      <!-- radio -->
      <fieldset :disabled="content.input">
        <ul v-if="inputType === 'radio'">
          <li v-for="(option, idx) in content.options" :key="idx" class="mb-6">
            <!-- Object -->
            <input
              v-if="option && option.mark"
              :id="`${content.$id}_${idx}_${_uid}`"
              v-model="picked"
              :class="['hidden', getChecked(content.input, option.mark)]"
              type="radio"
              :value="option.mark"
            />
            <!-- string -->
            <input
              v-else
              :id="`${content.$id}_${idx}_${_uid}`"
              v-model="picked"
              :class="['hidden', getChecked(content.input, option)]"
              type="radio"
              :value="option"
            />
            <label :for="`${content.$id}_${idx}_${_uid}`">
              <!-- Object -->
              <span v-if="option && option.mark">{{ `${option.mark}. ${option.content}` }}</span>
              <!-- string -->
              <span v-else>{{ option }}</span>
            </label>
          </li>
        </ul>
      </fieldset>

      <!-- checkbox -->
      <fieldset :disabled="content.input">
        <ul v-if="inputType === 'checkbox'">
          <li v-for="(option, idx) in content.options" :key="idx" class="mb-6">
            <input
              v-if="option && option.mark"
              :id="`${content.$id}_${idx}_${_uid}`"
              v-model="checkedNames"
              :class="['hidden', getChecked(content.input, option.mark)]"
              type="checkbox"
              :value="option.mark"
            />
            <input
              v-else
              :id="`${content.$id}_${idx}_${_uid}`"
              v-model="checkedNames"
              :class="['hidden', getChecked(content.input, option)]"
              type="checkbox"
              :value="option"
            />
            <label :for="`${content.$id}_${idx}_${_uid}`">
              <!-- Object -->
              <span v-if="option && option.mark">{{ `${option.mark}. ${option.content}` }}</span>
              <!-- string -->
              <span v-else>{{ option }}</span>
            </label>
          </li>
        </ul>
      </fieldset>

      <transition name="fade" mode="out-in">
        <button
          v-if="!content.input"
          type="submit"
          :disabled="content.input"
          class="block w-full px-4 py-2.5 mt-2 rounded-md cursor-pointer border border-primar bg-motif hover:opacity-hover active:opacity-active text-white"
        >
          确定
        </button>
        <div
          v-if="content.input && (content.answer || content.analysis)"
          class="answer px-4 py-2 rounded-md font-medium border border-gray-300 bg-white"
        >
          <div class="answer-label">解析</div>
          <hr class="my-3" />
          <div v-if="content.answer" class="answer">
            <span>正确答案:</span>
            <span v-for="answer in content.answer" :key="answer" class="ml-2">{{ answer }}</span>
          </div>
          <div v-if="content.analysis" class="analysis" v-html="markdownAnalysis"></div>
        </div>
      </transition>
    </form>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { Content } from '@forchange/storytime';
import { namespace } from 'vuex-class';
import { Score } from '@/store/modules/common/types';
import { OptionScore, SurveyOptions } from './index.vue';
import { marked } from 'marked';
const userModule = namespace('common');

export type Type = 'radio' | 'checkbox' | '单选' | '多选';

@Component({
  name: 'Choice'
})
export default class Choice extends Vue {
  @Prop({ type: Object }) content!: Content & {
    type: Type;
    title: string;
    options: SurveyOptions;
    answer?: Array<string>;
    analysis: string;
    input: Array<string>;
    startTime: number;
    reviewMark?: string;
    maxSelect: number;
    minSelect: number;
  };
  @userModule.State('score') score!: Score;

  picked = '';
  checkedNames: Array<string> = [];

  get inputType() {
    const typeMap = {
      radio: 'radio',
      checkbox: 'checkbox',
      单选: 'radio',
      多选: 'checkbox'
    };
    return typeMap[this.content.type];
  }

  get isRight() {
    // 不存在答案
    if (!this.content.answer) {
      return true;
    }

    if (this.inputType === 'radio') {
      // 恢复的数据判断
      if (this.content.input) {
        return this.content.answer.includes(this.content.input[0]);
      }
      // 及时数据判断
      return this.content.answer.includes(this.picked);
    }

    if (this.inputType === 'checkbox') {
      // 恢复的数据判断
      if (this.content.input) {
        return (
          this.content.answer.filter((v) => this.content.input.includes(v)).length ===
            this.content.answer.length && this.content.input.length === this.content.answer.length
        );
      }
      // 及时数据判断
      return (
        this.content.answer.filter((v) => this.checkedNames.includes(v)).length ===
          this.content.answer.length && this.checkedNames.length === this.content.answer.length
      );
    }
  }

  get warningClass() {
    if (!this.content.input) return 'blank';
    if (this.isRight) {
      return 'success';
    } else {
      return 'warning';
    }
  }

  get totalScore() {
    // 多维不记录单题总分数
    if (this.score.type === 'multiple') return 0;
    return this.content.options.reduce((total: number, option) => {
      if (typeof option === 'string') return 0;
      if (typeof option.score === 'object') {
        return 0;
      }
      return total + (option.score || 0);
    }, 0);
  }

  get markdownAnalysis() {
    return marked.parse(this.content.analysis);
  }

  @Watch('checkedNames')
  maxLength() {
    if (!this.content.maxSelect) return;
    this.$nextTick(() => {
      if (this.checkedNames.length > this.content.maxSelect) {
        this.checkedNames.pop();
      }
    });
  }

  getChecked(input: Array<string>, item: string) {
    if (!input) return '';
    return input.includes(item) ? 'checked' : '';
  }

  handleSubmit() {
    if (this.inputType === 'checkbox' && this.content.minSelect > this.checkedNames.length) {
      this.$Message.info({
        time: 1500,
        content: `请至少选择${this.content.minSelect}个选项`
      });
      return;
    }
    // check empty
    if (this.checkedNames.length === 0 && !this.picked) {
      this.$Message.info({
        time: 1500,
        content: '要先回答才能继续哦'
      });
      return;
    }
    const score = this.countScore();
    this.markInput();
    this.$emit('submit', {
      score,
      totalScore: this.totalScore,
      duration: new Date().getTime() - (this.content.startTime || 0)
    });
    if (this.score.status) {
      this.insertScore(score);
    }
  }

  countScore(): number | Map<string, number> {
    if (!this.isRight) return 0;
    if (this.content.answer) return this.totalCountScore();
    if (this.inputType === 'radio')
      return this.selectCountScore((item) => {
        return typeof item !== 'string' && item.mark === this.picked;
      });
    if (this.inputType === 'checkbox')
      return this.selectCountScore((item) => {
        return typeof item !== 'string' && this.checkedNames.includes(item.mark);
      });
    return 0;
  }

  insertScore(score: number | Map<string, number>) {
    let scoreValue: any;
    if (typeof score === 'object') {
      scoreValue = {};
      score.forEach((value, key) => {
        scoreValue[key] = value;
      });
    } else {
      scoreValue = score;
    }
    window.storyteller.updateAssignContent({
      $id: this.content.$id,
      $kind: this.content.$kind,
      ...{
        score: scoreValue
      }
    });
  }

  totalCountScore(): number | Map<string, number> {
    if (this.score.type === 'single') return this.totalScore;
    const scoreMap = new Map<string, number>();
    this.content.options.forEach((item) => {
      if (typeof item.score !== 'object') return this.multipleScoreError(item);
      item.score.forEach((scoreOptions) => {
        scoreMap.set(
          scoreOptions.type,
          scoreOptions.value + (scoreMap.get(scoreOptions.type) || 0)
        );
      });
    });
    return scoreMap;
  }

  selectCountScore(
    permitCall: (itme: { mark: string; content: string; score: OptionScore }) => boolean
  ): number | Map<string, number> {
    if (this.score.type === 'single') {
      return this.content.options.reduce((total: number, item) => {
        if (typeof item.score === 'object') {
          this.singleScoreError(item);
          return 0;
        }
        if (permitCall(item)) return total + (item.score || 0);
        return total;
      }, 0);
    } else {
      const scoreMap = new Map<string, number>();
      this.content.options.forEach((item) => {
        if (typeof item.score !== 'object') return;
        item.score.forEach((scoreOptions) => {
          if (permitCall(item)) {
            scoreMap.set(
              scoreOptions.type,
              scoreOptions.value + (scoreMap.get(scoreOptions.type) || 0)
            );
          }
        });
      });
      return scoreMap;
    }
  }

  singleScoreError(option: { mark: string; content: string }) {
    this.$Message.error({
      content: `单一计分模式下请勿书写多维计分 选项：${option.mark}${option.content}`,
      time: 3000
    });
  }

  multipleScoreError(option: { mark: string; content: string }) {
    this.$Message.error({
      content: `多维度计分模式下请勿书写单一计分 选项：${option.mark}${option.content}`,
      time: 3000
    });
  }

  markInput() {
    if (this.inputType === 'radio') {
      window.storyteller.input([this.picked]);
    }
    if (this.inputType === 'checkbox') {
      window.storyteller.input(this.checkedNames);
    }
    window.storyteller.updateAssignContent({
      $id: this.content.$id,
      $kind: this.content.$kind,
      ...{
        duration: new Date().getTime() - (this.content.startTime || 0),
        selection_id: this.score.selection_id
      }
    });
    this.saveReviewData();
  }

  saveReviewData() {
    if (!this.content.reviewMark) return;
    this.$store.dispatch('survey/setData', {
      contentId: this.content.$id.split('#')[0],
      mark: this.content.reviewMark,
      content: this.getValue()
    });
  }

  getValue() {
    return {
      type: 'choice',
      title: this.content.title,
      content: {
        picked: this.picked,
        pickedContent: this.content.options
          .filter((item) => {
            if (typeof item === 'string') {
              return item === this.picked;
            }
            return item.mark === this.picked;
          })
          .map((item) => {
            if (typeof item === 'string') {
              return item;
            }
            return item.content;
          }),
        checkedNames: this.checkedNames,
        checkedNameContent: this.content.options
          .filter((item) => {
            if (typeof item === 'string') {
              return this.checkedNames.includes(item);
            }
            return this.checkedNames.includes(item.mark);
          })
          .map((item) => {
            if (typeof item === 'string') {
              return item;
            }
            return item.content;
          })
      }
    };
  }

  hiddenCard() {
    this.$store.commit('common/setScore', {
      visible: false
    });
  }
}
</script>

<style scoped>
.title-text {
  color: #1a051d;
}
.label {
  @apply mb-4 flex text-motif-500;
}

.content input:checked + label,
.content .checked + label {
  @apply text-motif-500 border-motif-500;
  background-color: rgba(64, 71, 245, 0.05);
}

.content.warning input:checked + label,
.content.warning .checked + label {
  color: #666666;
  background-color: rgba(255, 0, 0, 0.1);
  border-color: #ed6559;
}

.content.success input:checked + label,
.content.success .checked + label {
  color: #666666;
  background: rgba(94, 205, 139, 0.06);
  border-color: #5ecd8b;
}

.content label {
  @apply block px-4 py-2.5 rounded-md cursor-pointer font-normal;
  color: #666666;
  border: 1px solid #d0d0d6;
}

@screen md {
  .blank label:hover {
    @apply bg-base;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.answer-label {
  @apply font-medium text-lg;
  color: rgba(64, 71, 245, 0.8);
}

.answer {
  @apply mb-3 font-medium text-lg;
  color: #272564;
}

.analysis {
  @apply font-normal;
  color: #666;
}

.analysis /deep/ pre {
  display: block;
  margin: 5px 0;
  padding: 16px;
  overflow: auto;
  font-size: 85%;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-all;
  background-color: #f6f8fa;
  border-radius: 6px;
}
.analysis /deep/ pre code {
  color: #000;
}
</style>
